import React, { Suspense, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import AuthGuard from '@/components/AuthGuard';
import { ReportRouteGuard } from '@/features/storyboard-report/components/ReportRouteGuard';
import Layout from '@/components/Layout';
import { Spinner } from '@/components/ui/Spinner';
import { useTheme } from '@/hooks/useTheme';
import { UpdateBanner } from '@/components/UpdateBanner';
import { useAlertNotifications } from '@/hooks/useAlertNotifications';
import { useSSE } from '@/hooks/useSSE';
import { useZoomWarning } from '@/hooks/useZoomWarning';
import { usePwaInstallGuide } from '@/hooks/usePwaInstallGuide';
import { lazyWithRecovery } from '@/lib/lazy-with-recovery';
import { NavigationReloadGate } from '@/components/NavigationReloadGate';

// Lazy-loaded pages — 배포 후 옛 청크 404는 lazyWithRecovery가 1회 조용한 리로드로 흡수 (lib/chunk-recovery.ts)
const LoginPage = lazyWithRecovery(() => import('@/features/auth/LoginPage'));
const RegisterPage = lazyWithRecovery(() => import('@/features/auth/RegisterPage'));
const InstallGuidePage = lazyWithRecovery(() => import('@/features/install/InstallGuidePage'));
const DashboardPage = lazyWithRecovery(() => import('@/features/dashboard/DashboardPage'));
const ProjectListPage = lazyWithRecovery(() => import('@/features/projects/ProjectListPage'));
const ProjectFormPage = lazyWithRecovery(() => import('@/features/projects/ProjectFormPage'));
const ProjectDetailPage = lazyWithRecovery(() => import('@/features/projects/ProjectDetailPage'));
const PersonalTaskPage = lazyWithRecovery(() => import('@/features/tasks/PersonalTaskPage'));
const GanttChartPage = lazyWithRecovery(() => import('@/features/gantt/GanttChartPage'));
const AdminPage = lazyWithRecovery(() => import('@/features/admin/AdminPage'));
const SettingsPage = lazyWithRecovery(() => import('@/features/settings/SettingsPage'));
const HelpGuidePage = lazyWithRecovery(() => import('@/features/guide/HelpGuidePage'));
const StoryboardPage = lazyWithRecovery(() => import('@/features/storyboard/StoryboardPage'));
const StoryboardReportPage = lazyWithRecovery(() => import('@/features/storyboard-report/StoryboardReportPage'));
const AgendaListPage = lazyWithRecovery(() => import('@/features/agendas/AgendaListPage'));
const AgendaDetailPage = lazyWithRecovery(() => import('@/features/agendas/AgendaDetailPage'));
const IssueReportListPage = lazyWithRecovery(() => import('@/features/issue-reports/IssueReportListPage'));
const IssueReportDetailPage = lazyWithRecovery(() => import('@/features/issue-reports/IssueReportDetailPage'));
const ProjectRequestListPage = lazyWithRecovery(() => import('@/features/project-requests/ProjectRequestListPage'));
const ProjectRequestDetailPage = lazyWithRecovery(() => import('@/features/project-requests/ProjectRequestDetailPage'));
const PatchNotesPage = lazyWithRecovery(() => import('@/features/patch-notes/PatchNotesPage'));
const NoticeListPage = lazyWithRecovery(() => import('@/features/notices/NoticeListPage'));
const NoticeDetailPage = lazyWithRecovery(() => import('@/features/notices/NoticeDetailPage'));
const NotificationsPage = lazyWithRecovery(() => import('@/features/notifications/NotificationsPage'));


function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[40vh] gap-2">
      <Spinner size="md" />
      <span className="text-gray-400 text-sm">페이지 로딩 중...</span>
    </div>
  );
}

/**
 * BrowserRouter 내부에서만 useLocation()을 호출할 수 있으므로
 * RouteErrorBoundary 래퍼를 별도 컴포넌트로 분리한다.
 * resetKey에 location.key를 전달해 라우트 이동 시 에러 상태를 자동 리셋한다.
 */
function RouteErrorBoundaryWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <RouteErrorBoundary resetKey={location.key}>
      {children}
    </RouteErrorBoundary>
  );
}

function AppShell() {
  useTheme();
  useAlertNotifications();
  useSSE();
  useZoomWarning();
  usePwaInstallGuide();

  return (
    <>
      <UpdateBanner />
      <BrowserRouter>
        {/* 새 버전 감지 후 첫 화면 이동(pathname 변경)을 전체 로드로 바꾼다 — Suspense 바깥 */}
        <NavigationReloadGate />
        <Suspense fallback={<PageLoader />}>
          <RouteErrorBoundaryWrapper>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/install" element={<InstallGuidePage />} />

            {/* Protected routes */}
            <Route
              element={
                <AuthGuard>
                  <Layout />
                </AuthGuard>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/projects" element={<ProjectListPage />} />
              <Route path="/projects/new" element={<ProjectFormPage />} />
              <Route path="/projects/:id" element={<ProjectDetailPage />} />
              <Route path="/projects/:id/edit" element={<ProjectFormPage />} />
              <Route path="/my-tasks" element={<PersonalTaskPage />} />
              <Route path="/gantt" element={<GanttChartPage />} />
              <Route path="/notices" element={<NoticeListPage />} />
              <Route path="/notices/:id" element={<NoticeDetailPage />} />
              <Route path="/agendas" element={<AgendaListPage />} />
              <Route path="/agendas/:id" element={<AgendaDetailPage />} />
              <Route path="/issue-reports" element={<IssueReportListPage />} />
              <Route path="/issue-reports/:id" element={<IssueReportDetailPage />} />
              <Route path="/project-requests" element={<ProjectRequestListPage />} />
              <Route path="/project-requests/:id" element={<ProjectRequestDetailPage />} />
              <Route path="/patch-notes" element={<PatchNotesPage />} />
              <Route
                path="/admin"
                element={
                  <AuthGuard requiredRoles={['master', 'admin']}>
                    <AdminPage />
                  </AuthGuard>
                }
              />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/storyboard" element={<StoryboardPage />} />
              <Route
                path="/storyboard/report"
                element={
                  <ReportRouteGuard>
                    <StoryboardReportPage />
                  </ReportRouteGuard>
                }
              />
              <Route path="/guide" element={<HelpGuidePage />} />
            </Route>
          </Routes>
          </RouteErrorBoundaryWrapper>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </>
  );
}

export default function App() {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        staleTime: 5 * 60 * 1000,
        gcTime: 15 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
    },
  }));

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppShell />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
